import type { RefObject } from "react";
import { ExternalLinkIcon, GearIcon } from "@radix-ui/react-icons";
import {
  Box,
  Button,
  Card,
  Flex,
  IconButton,
  Link,
  Spinner,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";

import {
  PlannerRepositoryActivity,
  type PanelCommit,
  type PanelPullRequest,
} from "@/components/planner-repository-activity";
import type { WeeklyPlannerRepositoryLink, WeeklyPlannerRow } from "@/lib/api";

type SaveState = "idle" | "saving" | "saved" | "error";

export function PlannerInlinePanel({
  openRowData,
  panelTop,
  panelRef,
  panelRepositories,
  panelPullRequests,
  panelCommits,
  repositoryActivityLoading = false,
  repositoryActivityError = null,
  canEditPanelProjectMetadata,
  leaderOptions,
  projectedEndDate,
  projectOwnerId,
  projectManagerId,
  frontendLeadId,
  backendLeadId,
  metadataSaveState,
  taskNotes,
  invoicingNotes,
  notesDirty,
  notesSaveState,
  onProjectedEndDateChange,
  onProjectOwnerChange,
  onProjectManagerChange,
  onFrontendLeadChange,
  onBackendLeadChange,
  onTaskNotesChange,
  onInvoicingNotesChange,
  onPersistRowNote,
  onOpenProjectSettings,
}: {
  openRowData: WeeklyPlannerRow;
  panelTop: number;
  panelRef: RefObject<HTMLDivElement>;
  panelRepositories: WeeklyPlannerRepositoryLink[];
  panelPullRequests: PanelPullRequest[];
  panelCommits: PanelCommit[];
  repositoryActivityLoading?: boolean;
  repositoryActivityError?: string | null;
  canEditPanelProjectMetadata: boolean;
  leaderOptions: Array<{ id: number; email: string }>;
  projectedEndDate: string;
  projectOwnerId: string;
  projectManagerId: string;
  frontendLeadId: string;
  backendLeadId: string;
  metadataSaveState: SaveState;
  taskNotes: string;
  invoicingNotes: string;
  notesDirty: boolean;
  notesSaveState: SaveState;
  onProjectedEndDateChange: (value: string) => void;
  onProjectOwnerChange: (value: string) => void;
  onProjectManagerChange: (value: string) => void;
  onFrontendLeadChange: (value: string) => void;
  onBackendLeadChange: (value: string) => void;
  onTaskNotesChange: (value: string) => void;
  onInvoicingNotesChange: (value: string) => void;
  onPersistRowNote: () => void;
  onOpenProjectSettings: (projectId: number) => void;
}) {
  return (
    <div
      ref={panelRef}
      className="weekly-planner-inline-panel"
      style={{
        position: "absolute",
        left: "0",
        right: "0",
        top: `${panelTop}px`,
      }}
    >
      <Card>
        <Box p="3" style={{ position: "relative", paddingRight: "12px" }}>
          {openRowData.row_kind === "project" ? (
            <IconButton
              variant="soft"
              size="1"
              aria-label="Open project settings"
              onClick={() => onOpenProjectSettings(openRowData.row_entity_id)}
              style={{ position: "absolute", top: "8px", right: "8px" }}
            >
              <GearIcon />
            </IconButton>
          ) : null}
          <Flex direction="column" gap="3">
            {openRowData.row_kind === "project" ? (
              <Flex direction="column" gap="3" className="my-projects-panel-block">
                <Flex gap="3" wrap="wrap">
                  <Box style={{ flex: "1 1 100%", minWidth: "300px" }}>
                    <Flex gap="2" wrap="wrap">
                      <IntegrationLink label="Harvest Project" href={openRowData.harvest_url} />
                      <IntegrationLink label="Asana Project" href={openRowData.asana_url} />
                      <IntegrationLink label="Files" href={null} emptyText="Files" />
                      {panelRepositories.map((repository, index) => (
                        <IntegrationLink
                          key={`${repository.name}-${index}`}
                          label={repository.name || "Repository"}
                          href={repository.url}
                          emptyText={`${repository.name || "Repository"} not configured`}
                        />
                      ))}
                    </Flex>
                  </Box>
                </Flex>
                <Flex gap="3" wrap="wrap" className="planner-inline-metadata-row">
                  <Box style={{ flex: "1 1 360px", minWidth: "300px" }}>
                    <Flex
                      gap="2"
                      wrap="wrap"
                      className="planner-inline-fields-row planner-inline-dates-row"
                    >
                      <label className="planner-inline-select-label planner-inline-select-label--compact">
                        <span>Start Date (Harvest)</span>
                        <TextField.Root
                          aria-label="Project Start Date (Harvest)"
                          value={
                            openRowData?.start_date
                              ? openRowData.start_date.slice(0, 10)
                              : "Not set in Harvest"
                          }
                          readOnly
                        />
                      </label>
                      <label className="planner-inline-select-label planner-inline-select-label--compact">
                        <span>End Date</span>
                        <TextField.Root
                          type="date"
                          aria-label="Projected End Date"
                          value={projectedEndDate}
                          disabled={!canEditPanelProjectMetadata}
                          onChange={(event) => onProjectedEndDateChange(event.target.value)}
                        />
                      </label>
                    </Flex>
                  </Box>
                  <Box style={{ flex: "1 1 360px", minWidth: "300px" }}>
                    {canEditPanelProjectMetadata ? (
                      <div className="planner-inline-roles-grid">
                        <label className="planner-inline-select-label planner-inline-select-label--compact">
                          <span>Project Owner</span>
                          <select
                            aria-label="Project Owner"
                            value={projectOwnerId}
                            onChange={(event) => onProjectOwnerChange(event.target.value)}
                          >
                            <option value="">-- Unassigned --</option>
                            {leaderOptions.map((user) => (
                              <option key={`owner-${user.id}`} value={user.id.toString()}>
                                {user.email}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="planner-inline-select-label planner-inline-select-label--compact">
                          <span>Project Manager</span>
                          <select
                            aria-label="Project Manager"
                            value={projectManagerId}
                            onChange={(event) => onProjectManagerChange(event.target.value)}
                          >
                            <option value="">-- Unassigned --</option>
                            {leaderOptions.map((user) => (
                              <option key={`manager-${user.id}`} value={user.id.toString()}>
                                {user.email}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="planner-inline-select-label planner-inline-select-label--compact">
                          <span>Frontend Lead</span>
                          <select
                            aria-label="Frontend Lead"
                            value={frontendLeadId}
                            onChange={(event) => onFrontendLeadChange(event.target.value)}
                          >
                            <option value="">-- Unassigned --</option>
                            {leaderOptions.map((user) => (
                              <option key={`fe-${user.id}`} value={user.id.toString()}>
                                {user.email}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="planner-inline-select-label planner-inline-select-label--compact">
                          <span>Backend Lead</span>
                          <select
                            aria-label="Backend Lead"
                            value={backendLeadId}
                            onChange={(event) => onBackendLeadChange(event.target.value)}
                          >
                            <option value="">-- Unassigned --</option>
                            {leaderOptions.map((user) => (
                              <option key={`be-${user.id}`} value={user.id.toString()}>
                                {user.email}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    ) : (
                      <Flex direction="column" gap="1" className="planner-inline-readonly-leads">
                        <Text size="1">PO: {openRowData.project_owner_email || "Unassigned"}</Text>
                        <Text size="1">
                          PM: {openRowData.project_manager_email || "Unassigned"}
                        </Text>
                        <Text size="1">FE: {openRowData.frontend_lead_email || "Unassigned"}</Text>
                        <Text size="1">BE: {openRowData.backend_lead_email || "Unassigned"}</Text>
                      </Flex>
                    )}
                  </Box>
                </Flex>
                <Text
                  size="1"
                  color={metadataSaveState === "error" ? "red" : "gray"}
                  role="status"
                  aria-live="polite"
                >
                  {metadataSaveState === "saving" && "Saving project metadata..."}
                  {metadataSaveState === "saved" && "Project metadata saved"}
                  {metadataSaveState === "error" && "Project metadata save failed"}
                  {!canEditPanelProjectMetadata &&
                    metadataSaveState === "idle" &&
                    "Only administrators and project managers can edit project end date or leads."}
                </Text>
              </Flex>
            ) : null}
            <Flex gap="3" wrap="wrap">
              <Box style={{ flex: "1 1 360px", minWidth: "300px" }}>
                <Text as="label" size="1" weight="medium">
                  Task Notes
                </Text>
                <TextArea
                  aria-label="Task Notes"
                  size="1"
                  rows={7}
                  value={taskNotes}
                  onChange={(event) => onTaskNotesChange(event.target.value)}
                  onBlur={() => {
                    if (!notesDirty) return;
                    onPersistRowNote();
                  }}
                  placeholder="Task Notes"
                />
              </Box>
              <Box style={{ flex: "1 1 360px", minWidth: "300px" }}>
                <Text as="label" size="1" weight="medium">
                  Invoicing Notes
                </Text>
                <TextArea
                  aria-label="Invoicing Notes"
                  size="1"
                  rows={7}
                  value={invoicingNotes}
                  onChange={(event) => onInvoicingNotesChange(event.target.value)}
                  onBlur={() => {
                    if (!notesDirty) return;
                    onPersistRowNote();
                  }}
                  placeholder="Invoicing Notes"
                />
              </Box>
            </Flex>
            <Text
              size="1"
              color={notesSaveState === "error" ? "red" : "gray"}
              role="status"
              aria-live="polite"
            >
              {notesSaveState === "saving" && "Saving notes..."}
              {notesSaveState === "saved" && "Notes saved"}
              {notesSaveState === "error" && "Notes save failed"}
            </Text>
            {openRowData.row_kind === "project" ? (
              <Flex direction="column" gap="2">
                {repositoryActivityLoading ? (
                  <Flex align="center" gap="2">
                    <Spinner size="1" />
                    <Text size="1" color="gray">
                      Loading repository activity…
                    </Text>
                  </Flex>
                ) : null}
                {repositoryActivityError ? (
                  <Text size="1" color="red">
                    {repositoryActivityError}
                  </Text>
                ) : null}
                <PlannerRepositoryActivity
                  pullRequests={panelPullRequests}
                  commits={panelCommits}
                />
              </Flex>
            ) : null}
            {openRowData.row_kind === "roll_up_group" ? (
              <Box>
                <Text size="1" weight="medium" color="gray">
                  Included Harvest Projects:{" "}
                </Text>
                {openRowData.included_harvest_projects &&
                openRowData.included_harvest_projects.length > 0 ? (
                  <Text size="1" className="weekly-planner-rollup-projects-list">
                    {openRowData.included_harvest_projects.join(", ")}
                  </Text>
                ) : (
                  <Text size="1" color="gray">
                    No mapped Harvest projects found for this roll-up.
                  </Text>
                )}
              </Box>
            ) : null}
          </Flex>
        </Box>
      </Card>
    </div>
  );
}

function IntegrationLink({
  label,
  href,
  compact = false,
  emptyText,
}: {
  label: string;
  href: string | null | undefined;
  compact?: boolean;
  emptyText?: string;
}) {
  if (!href) {
    return (
      <Button
        size={compact ? "1" : "2"}
        variant={compact ? "soft" : "outline"}
        disabled
        className="my-projects-link-button my-projects-link-button--empty"
      >
        {emptyText || `${label} not configured`}
      </Button>
    );
  }

  return (
    <Button
      asChild
      size={compact ? "1" : "2"}
      variant={compact ? "soft" : "outline"}
      className="my-projects-link-button"
    >
      <Link href={href} target="_blank" rel="noreferrer">
        {label}
        <ExternalLinkIcon aria-hidden="true" />
      </Link>
    </Button>
  );
}
